import { resolveCampaignCompletion } from './campaignCompletion';

export const RECENT_COMPLETION_WINDOW_MS = 10 * 24 * 60 * 60 * 1000;

export const DASHBOARD_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'needs_action', label: 'Needs Action' },
  { value: 'pending_send', label: 'Pending Send' },
  { value: 'live', label: 'Live' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'completed', label: 'Completed Campaigns' },
  { value: 'archived', label: 'Archived' }
];

function normalizedStatus(client = {}) {
  return String(client.status || client.campaignStatus || '').trim().toLowerCase();
}

export function isArchivedDashboardClient(client = {}) {
  return Boolean(
    client.archived ||
    client.isArchived ||
    normalizedStatus(client) === 'archived'
  );
}

export function readDashboardTimestampMillis(value) {
  if (!value) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime()) ? date.getTime() : 0;
  }
  if (typeof value.seconds === 'number') {
    return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1000000);
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getPendingSendCount(client = {}, meta = {}) {
  return Number(
    meta.pendingSendCount ||
    client.pendingOutboxCount ||
    client.outboxCount ||
    client.pendingSendCount ||
    0
  );
}

export function clientMatchesStatusFilter(client, filterValue, meta = {}) {
  const status = normalizedStatus(client);
  const currentActionCount = Number(meta.currentActionCount || 0);
  const pendingSendCount = getPendingSendCount(client, meta);
  const campaignCompletion = meta.campaignCompletion || resolveCampaignCompletion(client, meta);
  const isCampaignComplete = Boolean(campaignCompletion?.isCampaignComplete);
  const isArchived = isArchivedDashboardClient(client);

  switch (filterValue) {
    case 'all':
      return true;
    case 'needs_action':
      return currentActionCount > 0 || status === 'new';
    case 'pending_send':
      return pendingSendCount > 0 || status === 'pending_send' || status === 'pending';
    case 'live':
      return ['active', 'live', 'running', 'in_progress'].includes(status) &&
        !isArchived &&
        !isCampaignComplete &&
        currentActionCount === 0 &&
        pendingSendCount === 0;
    case 'stopped':
      return ['stopped', 'paused', 'cancelled', 'canceled'].includes(status);
    case 'completed':
      return isCampaignComplete ||
        ['completed', 'complete', 'closed', 'done'].includes(status);
    case 'archived':
      return isArchived;
    default:
      return true;
  }
}

const STOPPED_DASHBOARD_STATUSES = ['stopped', 'paused', 'cancelled', 'canceled'];
const COMPLETED_DASHBOARD_STATUSES = ['completed', 'complete', 'closed', 'done'];

export function isStoppedDashboardClient(client = {}) {
  return STOPPED_DASHBOARD_STATUSES.includes(normalizedStatus(client));
}

// The tiles read the campaigns the rows read, and they read the campaign's own
// status. A campaign whose properties all happen to be resolved is still live
// until the advisor completes or stops it, so it counts as active here.
export function isCompletedDashboardClientStatus(client = {}) {
  return COMPLETED_DASHBOARD_STATUSES.includes(normalizedStatus(client));
}

export function countActiveDashboardClients(clients = []) {
  return clients.filter(client => (
    !isArchivedDashboardClient(client)
    && !isStoppedDashboardClient(client)
    && !isCompletedDashboardClientStatus(client)
  )).length;
}

export function countCompletedDashboardClients(clients = []) {
  return clients.filter(client => (
    !isArchivedDashboardClient(client)
    && (isStoppedDashboardClient(client) || isCompletedDashboardClientStatus(client))
  )).length;
}

function readCompletionTimestampMillis(client = {}, campaignCompletion = {}) {
  const timestampCandidates = [
    campaignCompletion.completedAt,
    campaignCompletion.completed_at,
    campaignCompletion.completedOn,
    campaignCompletion.lastResolvedAt,
    campaignCompletion.updatedAt,
    client.campaignCompletedAt,
    client.completedAt,
    client.completed_at,
    client.completedOn,
    client.lastCompletedAt,
    client.updatedAt,
    client.lastUpdated,
    client.createdAt
  ];

  for (const value of timestampCandidates) {
    const millis = readDashboardTimestampMillis(value);
    if (millis > 0) return millis;
  }

  return 0;
}

function readClientActivityTimestampMillis(client = {}) {
  const timestampCandidates = [
    client.updatedAt,
    client.lastUpdated,
    client.createdAt
  ];

  for (const value of timestampCandidates) {
    const millis = readDashboardTimestampMillis(value);
    if (millis > 0) return millis;
  }

  return 0;
}

function isCompletedDashboardClient(client = {}, campaignCompletion = {}) {
  const status = normalizedStatus(client);
  return Boolean(campaignCompletion?.isCampaignComplete) ||
    ['completed', 'complete', 'closed', 'done'].includes(status);
}

function getDashboardSortBucket(client = {}, campaignCompletion = {}, nowMillis = Date.now()) {
  if (!isCompletedDashboardClient(client, campaignCompletion)) return 1;

  const completedAtMillis = readCompletionTimestampMillis(client, campaignCompletion);
  const recentBoundary = nowMillis - RECENT_COMPLETION_WINDOW_MS;
  return completedAtMillis >= recentBoundary ? 0 : 2;
}

export function sortDashboardClients(clients = [], {
  clientCampaignCompletion = {},
  now = new Date()
} = {}) {
  const nowMillis = readDashboardTimestampMillis(now) || Date.now();

  return [...clients].sort((a, b) => {
    const aCompletion = clientCampaignCompletion[a.id] || {};
    const bCompletion = clientCampaignCompletion[b.id] || {};
    const aBucket = getDashboardSortBucket(a, aCompletion, nowMillis);
    const bBucket = getDashboardSortBucket(b, bCompletion, nowMillis);

    if (aBucket !== bBucket) return aBucket - bBucket;

    const aTimestamp = aBucket === 1
      ? readClientActivityTimestampMillis(a)
      : readCompletionTimestampMillis(a, aCompletion);
    const bTimestamp = bBucket === 1
      ? readClientActivityTimestampMillis(b)
      : readCompletionTimestampMillis(b, bCompletion);

    if (aTimestamp !== bTimestamp) return bTimestamp - aTimestamp;

    const aName = String(a.name || '').toLowerCase();
    const bName = String(b.name || '').toLowerCase();
    if (aName !== bName) return aName.localeCompare(bName);

    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}
